import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Select, DatePicker, Table, Badge } from '@/components/ui';
import { DonutLegendChart } from '@/components/ui/DonutLegendChart';
import {
  Download,
  Settings,
  Filter,
  RefreshCw,
  FileText,
  CheckCircle,
  Clock,
  Database,
  Banknote,
  AlertTriangle,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('thi-cong');

  const donutData = [
    { name: 'Hoàn thành 3 dự án (17%)', value: 3, color: '#10b981' },
    { name: 'Đang thực hiện 12 dự án (67%)', value: 12, color: '#3b82f6' },
    { name: 'Chậm tiến độ 2 dự án (11%)', value: 2, color: '#f59e0b' },
    { name: 'Chưa bắt đầu 1 dự án (5%)', value: 1, color: '#94a3b8' },
  ];

  const valueByMonthData = [
    { month: 'T12/2023', plan: 12.5, actual: 12.5 },
    { month: '01/2024', plan: 18.7, actual: 18.7 },
    { month: '02/2024', plan: 24.3, actual: 24.3 },
    { month: '03/2024', plan: 33.8, actual: 33.8 },
    { month: '04/2024', plan: 42.1, actual: 42.1 },
    { month: '05/2024', plan: 48.6, actual: 48.6 },
  ];

  const completionByAreaData = [
    { area: 'Khu A', value: 80, color: '#10b981' },
    { area: 'Khu B', value: 72, color: '#10b981' },
    { area: 'Khu C', value: 65, color: '#f59e0b' },
    { area: 'Khu D', value: 48, color: '#f59e0b' },
    { area: 'Khu E', value: 30, color: '#ef4444' },
  ];

  const progressByCategoryData = [
    { id: '1', category: 'Kết cấu', total: 18, done: '4 (22%)', inProgress: '11 (61%)', late: '2 (11%)', notStarted: '1 (6%)', avgProgress: 75, status: 'good' },
    { id: '2', category: 'Kiến trúc', total: 18, done: '3 (17%)', inProgress: '12 (67%)', late: '2 (11%)', notStarted: '1 (5%)', avgProgress: 70, status: 'good' },
    { id: '3', category: 'Cơ điện (MEP)', total: 18, done: '2 (11%)', inProgress: '11 (61%)', late: '3 (17%)', notStarted: '2 (11%)', avgProgress: 65, status: 'warning' },
    { id: '4', category: 'Hạ tầng kỹ thuật', total: 18, done: '3 (17%)', inProgress: '9 (50%)', late: '4 (22%)', notStarted: '2 (11%)', avgProgress: 58, status: 'warning' },
    { id: '5', category: 'Cảnh quan', total: 18, done: '2 (11%)', inProgress: '8 (44%)', late: '4 (22%)', notStarted: '4 (23%)', avgProgress: 45, status: 'danger' },
  ];

  const topProjectsData = [
    { id: '01', name: 'Khu đô thị Green City', area: 'Khu A', value: 12.6, progress: 85, status: 'good' },
    { id: '02', name: 'Chung cư Skyline', area: 'Khu B', value: 8.7, progress: 78, status: 'good' },
    { id: '03', name: 'Tòa nhà Office Building', area: 'Khu A', value: 6.3, progress: 65, status: 'warning' },
    { id: '04', name: 'Trung tâm thương mại ABC', area: 'Khu C', value: 5.1, progress: 52, status: 'warning' },
    { id: '05', name: 'Nhà máy số 2', area: 'Khu D', value: 4.2, progress: 40, status: 'danger' },
  ];

  return (
    <PageLayout
      title="Báo cáo"
      description="Thống kê, phân tích và báo cáo tình hình thi công và bảo dưỡng"
    >
      <div className="space-y-4">
        {/* KPIs Section */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4 flex flex-col justify-between">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center bg-blue-50 text-blue-600">
                <FileText className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="sm:text-sm text-xs text-slate-500">Tổng số dự án</div>
                <div className="text-xl text-slate-800">18</div>
                <div className="text-[10px] text-slate-500">Dự án</div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <ArrowUp className="w-3 h-3" />
              12% so với kỳ trước
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4 flex flex-col justify-between">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center bg-emerald-50 text-emerald-600">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="sm:text-sm text-xs text-slate-500">Dự án đang thực hiện</div>
                <div className="text-xl text-slate-800">12</div>
                <div className="text-[10px] text-slate-500">Dự án</div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <ArrowUp className="w-3 h-3" />
              8% so với kỳ trước
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4 flex flex-col justify-between">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center bg-orange-50 text-orange-600">
                <Clock className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="sm:text-sm text-xs text-slate-500">Tiến độ trung bình</div>
                <div className="text-xl text-slate-800">72%</div>
                <div className="text-[10px] text-slate-500">% hoàn thành</div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <ArrowUp className="w-3 h-3" />
              5% so với kỳ trước
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4 flex flex-col justify-between">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center bg-purple-50 text-purple-600">
                <Database className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="sm:text-sm text-xs text-slate-500">Giá trị thực hiện</div>
                <div className="text-xl text-slate-800">48.6</div>
                <div className="text-[10px] text-slate-500">Tỷ VNĐ</div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <ArrowUp className="w-3 h-3" />
              15% so với kỳ trước
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4 flex flex-col justify-between">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center bg-teal-50 text-teal-600">
                <Banknote className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="sm:text-sm text-xs text-slate-500">Giá trị giải ngân</div>
                <div className="text-xl text-slate-800">32.4</div>
                <div className="text-[10px] text-slate-500">Tỷ VNĐ</div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <ArrowUp className="w-3 h-3" />
              10% so với kỳ trước
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4 flex flex-col justify-between">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center bg-rose-50 text-rose-600">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <div className="sm:text-sm text-xs text-slate-500">Vấn đề phát sinh</div>
                <div className="text-xl text-slate-800">16</div>
                <div className="text-[10px] text-slate-500">Vấn đề</div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-rose-600">
              <ArrowDown className="w-3 h-3" />
              5% so với kỳ trước
            </div>
          </div>
        </div>

        {/* Toolbar Section (Tabs, Actions, and Filters) */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Left: Navigation Tabs */}
            <div className="flex items-center gap-1 border-b border-slate-100 lg:border-none pb-2 lg:pb-0">
              <button
                onClick={() => setActiveTab('thi-cong')}
                className={`relative px-4 py-2 sm:text-sm text-xs font-normal transition-all ${
                  activeTab === 'thi-cong'
                    ? 'text-emerald-700 after:absolute after:bottom-[-8px] lg:after:bottom-[-12px] after:left-0 after:h-0.5 after:w-full after:bg-emerald-600'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Thi công
              </button>
              <button
                onClick={() => setActiveTab('bao-duong')}
                className={`relative px-4 py-2 sm:text-sm text-xs font-normal transition-all ${
                  activeTab === 'bao-duong'
                    ? 'text-emerald-700 after:absolute after:bottom-[-8px] lg:after:bottom-[-12px] after:left-0 after:h-0.5 after:w-full after:bg-emerald-600'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Bảo dưỡng
              </button>
            </div>
            
            {/* Right: Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" className="h-9 gap-2 sm:text-sm text-xs text-slate-700">
                <Download className="w-4 h-4" />
                Xuất báo cáo
              </Button>
              <Button variant="outline" className="h-9 gap-2 sm:text-sm text-xs text-slate-700">
                <Settings className="w-4 h-4" />
                Tùy chỉnh báo cáo
              </Button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full sm:w-64">
              <DatePicker
                placeholder="01/05/2024 - 31/05/2024"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={[{ label: 'Tất cả dự án', value: 'all' }]}
                value="all"
                onChange={() => {}}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={[{ label: 'Tất cả khu vực', value: 'all' }]}
                value="all"
                onChange={() => {}}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={[{ label: 'Tất cả nhà thầu', value: 'all' }]}
                value="all"
                onChange={() => {}}
              />
            </div>
            <Button variant="outline" className="text-slate-700 h-9 w-full sm:w-auto gap-2 sm:text-sm text-xs">
              <Filter className="w-4 h-4" />
              Bộ lọc
            </Button>
            
            <div className="flex-1" />
            
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Cập nhật: 22/05/2024 10:30</span>
              <button className="hover:text-slate-800 bg-white border border-slate-200 p-2 rounded-md"><RefreshCw className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <DonutLegendChart
            title="Tiến độ dự án"
            centerValue="18"
            centerLabel="Tổng dự án"
            data={donutData}
            total={18}
            className="h-[320px] -none"
          />
          
          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2 flex flex-col h-[320px]">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-slate-900">Giá trị thực hiện theo tháng</div>
              <Link to="/reports/construction/cost" className="text-xs text-blue-600 hover:underline">Xem chi tiết</Link>
            </div>
            <div className="flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={valueByMonthData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="actual" name="Thực hiện" fill="#10b981" barSize={16} radius={[2, 2, 0, 0]} />
                  <Line type="monotone" dataKey="plan" name="Kế hoạch" stroke="#94a3b8" strokeDasharray="5 5" dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2 flex flex-col h-[320px]">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-slate-900">Tỷ lệ hoàn thành theo khu vực</div>
              <Link to="/reports/construction/progress" className="text-xs text-blue-600 hover:underline">Xem chi tiết</Link>
            </div>
            <div className="flex-1 space-y-5 flex flex-col justify-center px-2">
              {completionByAreaData.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 text-xs text-slate-600">{item.area}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${item.value}%`, backgroundColor: item.color }} />
                  </div>
                  <div className="w-8 text-xs text-slate-600 text-right">{item.value}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tables Row */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
           <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-slate-900">Tiến độ dự án theo hạng mục</div>
              <Link to="/reports/construction/progress" className="text-xs text-blue-600 hover:underline">Xem chi tiết</Link>
            </div>
            <Table
              columns={[
                { key: 'category', title: 'Hạng mục' },
                { key: 'total', title: 'Tổng dự án', align: 'center' },
                { key: 'done', title: 'Hoàn thành', align: 'center' },
                { key: 'inProgress', title: 'Đang thực hiện', align: 'center' },
                { key: 'late', title: 'Chậm tiến độ', align: 'center' },
                { key: 'notStarted', title: 'Chưa bắt đầu', align: 'center' },
                { key: 'avgProgress', title: 'Tiến độ TB', align: 'center' },
              ]}
              data={progressByCategoryData}
              renderCell={(item: any, key) => {
                if (key === 'avgProgress') {
                  const colorClass = item.status === 'good' ? 'text-emerald-600' : item.status === 'warning' ? 'text-orange-600' : 'text-red-600';
                  return <span className={`text-xs ${colorClass}`}>{item[key]}%</span>;
                }
                if (key === 'category') {
                  return <span className="text-xs text-slate-800">{item[key]}</span>;
                }
                return <span className="text-xs text-slate-600">{item[key]}</span>;
              }}
            />
           </div>

           <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-slate-900">Top dự án theo giá trị thực hiện</div>
              <Link to="/reports/construction/cost" className="text-xs text-blue-600 hover:underline">Xem chi tiết</Link>
            </div>
            <Table
              columns={[
                { key: 'id', title: '#' },
                { key: 'name', title: 'Dự án' },
                { key: 'area', title: 'Khu vực' },
                { key: 'value', title: 'Giá trị thực hiện (Tỷ VNĐ)', align: 'center' },
                { key: 'progress', title: 'Tiến độ', align: 'center' },
              ]}
              data={topProjectsData}
              renderCell={(item: any, key) => {
                if (key === 'progress') {
                   const colorClass = item.status === 'good' ? 'text-emerald-600 bg-emerald-50' : item.status === 'warning' ? 'text-orange-600 bg-orange-50' : 'text-red-600 bg-red-50';
                   return <Badge className={`rounded-md border-none ${colorClass}`}>{item[key]}%</Badge>;
                }
                if (key === 'name') {
                  return <span className="text-xs text-slate-800">{item[key]}</span>;
                }
                return <span className="text-xs text-slate-600">{item[key]}</span>;
              }}
            />
           </div>
        </div>
      </div>
    </PageLayout>
  );
}
